import { useState, useEffect, useMemo } from 'react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

// Owns credit-memo data + the refund processing flow.
export const useCreditMemos = ({ activeTab }) => {
  const [creditMemos, setCreditMemos] = useState([]);
  const [creditMemoSummary, setCreditMemoSummary] = useState(null);
  const [selectedCreditMemo, setSelectedCreditMemo] = useState(null);
  const [creditMemoDetailOpen, setCreditMemoDetailOpen] = useState(false);
  const [loadingCreditMemos, setLoadingCreditMemos] = useState(false);

  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundMethods, setRefundMethods] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [selectedRefundMethod, setSelectedRefundMethod] = useState('');
  const [refundReference, setRefundReference] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);

  const loadCreditMemos = async () => {
    setLoadingCreditMemos(true);
    try {
      const [memos, summary, methods] = await Promise.all([
        adminService.getCreditMemos(),
        adminService.getCreditMemoSummary(),
        adminService.getRefundMethods(),
      ]);
      setCreditMemos(memos);
      setCreditMemoSummary(summary);
      setRefundMethods(methods || []);
    } catch (err) {
      console.error('Error loading credit memos:', err);
      toast.error('Failed to load credit memos');
    } finally {
      setLoadingCreditMemos(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'credit-memos') {
      loadCreditMemos();
    }
  }, [activeTab]);

  const manualRefundMethods = useMemo(
    () => refundMethods.filter(m => !m.is_stripe),
    [refundMethods]
  );

  const viewCreditMemo = (cm) => {
    setSelectedCreditMemo(cm);
    setCreditMemoDetailOpen(true);
  };

  const openRefundDialog = async (creditMemo) => {
    setSelectedCreditMemo(creditMemo);
    setLoadingPaymentInfo(true);
    setRefundDialogOpen(true);
    setSelectedRefundMethod('');
    setRefundReference('');
    setRefundNotes('');
    try {
      const info = await adminService.getCreditMemoPaymentInfo(creditMemo.id);
      setPaymentInfo(info.payment_info);
    } catch (err) {
      console.error('Error loading payment info:', err);
      setPaymentInfo(null);
    } finally {
      setLoadingPaymentInfo(false);
    }
  };

  const handleProcessRefund = async () => {
    if (!selectedRefundMethod) {
      toast.error('Please select a refund method');
      return;
    }
    setProcessingRefund(true);
    try {
      let result;
      if (selectedRefundMethod === 'stripe') {
        result = await adminService.processStripeRefund(selectedCreditMemo.id);
      } else {
        result = await adminService.processManualRefund(
          selectedCreditMemo.id,
          selectedRefundMethod,
          refundReference || null,
          null,
          refundNotes || null
        );
      }
      toast.success(result.message || 'Refund processed successfully');
      setRefundDialogOpen(false);
      setCreditMemoDetailOpen(false);
      loadCreditMemos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  return {
    creditMemos,
    creditMemoSummary,
    loadingCreditMemos,
    loadCreditMemos,
    selectedCreditMemo,
    creditMemoDetailOpen,
    setCreditMemoDetailOpen,
    viewCreditMemo,
    refundDialogOpen,
    setRefundDialogOpen,
    paymentInfo,
    loadingPaymentInfo,
    manualRefundMethods,
    selectedRefundMethod,
    setSelectedRefundMethod,
    refundReference,
    setRefundReference,
    refundNotes,
    setRefundNotes,
    processingRefund,
    openRefundDialog,
    handleProcessRefund,
  };
};
